import MarkdownIt from "markdown-it"; // Markdown转HTML解析器
import hljs from "highlight.js"; // 代码高亮库
import "highlight.js/styles/atom-one-dark.css"; // 代码高亮样式
import katex from "katex"; // 数学公式库
import mkatex from "markdown-it-katex"; // Markdown支持LaTeX公式
import "katex/dist/katex.min.css"; // 数学公式样式
import * as echarts from "echarts"; // ECharts图表库
import markdownItEcharts from "markdown-it-echarts"; // Markdown支持ECharts
import debounce from "lodash/debounce"; // 防抖函数，用于优化图表性能
import mermaid from "mermaid";

// 与 ChatPage 共用的 markdown-it 配置
const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  breaks: true,
  highlight: function (str, lang) {
    if (lang && hljs.getLanguage(lang)) {
      try {
        return `<div class="code-block">
                  <pre class="hljs"><code class="language-${lang}">${hljs.highlight(str, { language: lang, ignoreIllegals: true }).value
          }</code></pre>
                </div>`;
      } catch (__) {
        // 忽略错误，使用默认渲染
      }
    }
    return `<pre class="hljs"><code>${md.utils.escapeHtml(str)}</code></pre>`;
  },
});

md.use(markdownItEcharts, { echarts });
md.use(mkatex);

// 检查URL是否包含指定路径
function checkUrlContainsPath(url, targetPath) {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const pathSegments = pathname.split("/").filter((segment) => segment.trim() !== "");
    return pathSegments.includes(targetPath);
  } catch (err) {
    console.error("URL解析错误:", err);
    return false;
  }
}

// 初始化 Mermaid（仅客户端）
if (typeof window !== "undefined") {
  mermaid.initialize({
    startOnLoad: false,
    theme: "default",
    securityLevel: "loose",
    suppressErrorRendering: true,
  });
}

// 与 ChatPage.formatMessage 完全一致的 Markdown 渲染（含代码高亮、KaTeX、ECharts、链接规则等）
export function formatChatMessage(text, accessType, conversationId) {
  if (!text) return "";

  md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    const hrefIndex = token.attrIndex("href");
    if (hrefIndex >= 0) {
      const href = token.attrs[hrefIndex][1];
      const hasTarget = checkUrlContainsPath(href, "ai_open_file"); // 在线收集表
      const tpcLink = checkUrlContainsPath(href, "new_chat_tpc"); // 碳盘查
      const tzjLink = checkUrlContainsPath(href, "new_chat_tzj"); // 碳足迹
      const tpcQuestionNaireLink = checkUrlContainsPath(href, "tpc_qn_difficulty"); // 碳盘查问卷

      if (hasTarget) {
        const fileId = href.split("/").pop();
        const buttonHtml = `<span class="span-link" data-file-id="${fileId}" onclick="window.openExcelViewer('${fileId}')">${tokens[idx + 1].content}</span>`;
        // const buttonHtml = `<button class="excel-view-btn" data-file-id="${fileId}" onclick="window.openExcelViewer('${fileId}')">${tokens[idx + 1].content}</button>`;
        token.attrSet("is-special-link", "true");

        if (tokens[idx + 1] && tokens[idx + 1].type === "text") {
          tokens[idx + 1].content = "";
        }

        return buttonHtml;
      } else if(tpcQuestionNaireLink){
        const questionnaireId = href.split("/").pop();
        const buttonHtml = `<span class="span-link" data-questionnaire-id="${questionnaireId}" onclick="window.openQuestionNaire('${questionnaireId}')">${tokens[idx + 1].content}</span>`;
        token.attrSet("is-special-link", "true");

        if (tokens[idx + 1] && tokens[idx + 1].type === "text") {
          tokens[idx + 1].content = "";
        }

        return buttonHtml;
      } else if(tpcLink || tzjLink){
        const buttonHtml = `<button class="excel-view-btn"" onclick="window.newChat('${tpcLink ? 'tpc' : 'tzj'}', '${accessType}', '${conversationId}')">${tokens[idx + 1].content}</button>`;
        // const buttonHtml = `<div class="file-spile" onclick="window.newChat('${tpcLink ? 'tpc' : 'tzj'}', '${accessType}', '${conversationId}')">
        //   <div class="file-spile-pg">
        //     <div class="content">
        //       <div><svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" fill="none" viewBox="0 0 24 24"><path fill="#0057ff" fill-rule="evenodd" d="M20.014 9.046a.514.514 0 0 1-.512.454.52.52 0 0 1-.515-.452c-.241-1.445-.452-2.18-.906-2.634-.455-.453-1.186-.66-2.612-.9A.52.52 0 0 1 15 5.002c0-.275.206-.487.473-.513 1.422-.242 2.153-.454 2.607-.91s.666-1.191.907-2.623A.52.52 0 0 1 19.502.5c.273 0 .473.197.512.458.24 1.43.451 2.165.906 2.622.455.455 1.187.667 2.61.909.266.027.47.24.47.513a.52.52 0 0 1-.468.512c-1.425.24-2.157.452-2.612.907-.455.456-.666 1.192-.907 2.625M7 7.75A.75.75 0 0 1 7.75 7h2.5a.75.75 0 0 1 0 1.5h-2.5A.75.75 0 0 1 7 7.75m0 3a.75.75 0 0 1 .75-.75h6.5a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75M7.75 13a.75.75 0 0 0 0 1.5h8.5a.75.75 0 0 0 0-1.5zM19 21v-9.55c1.335-.276 1.744-.708 2-1.95V21a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h12.054c-.084.45-.158.754-.23.972-.05.151-.089.223-.105.25a1.3 1.3 0 0 1-.245.104c-.28.093-.703.19-1.4.309-.354.048-.69.175-.985.365H5v18z" clip-rule="evenodd"></path></svg></div>
        //       <div class="file-name">${tokens[idx + 1].content}</div>
        //       <div class="create-time">创建时间：2026/4/28</div>
        //     </div>
        //     <div class="file-tip">碳知AI智能文档</div>
        //   </div>
        // </div>`;
        if (tokens[idx + 1] && tokens[idx + 1].type === "text") {
          tokens[idx + 1].content = "";
        }

        return buttonHtml;
      } else {
        token.attrSet("target", "_blank");
        token.attrSet("rel", "noopener noreferrer");
      }
    }
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.link_close = (tokens, idx, options, env, self) => {
    const linkOpenToken = tokens[idx - 2];
    if (linkOpenToken && linkOpenToken.attrGet("is-special-link") === "true") {
      return "";
    }
    return self.renderToken(tokens, idx, options);
  };

  const renderMath = (content, displayMode) => {
    try {
      return katex.renderToString(content, { displayMode });
    } catch (error) {
      return displayMode ? `$$${content}$$` : `$${content}$`;
    }
  };

  md.renderer.rules.math_inline = function (tokens, idx) {
    const content = tokens[idx].content;
    try {
      return katex.renderToString(content, { displayMode: false });
    } catch (error) {
      return `$${content}$`;
    }
  };

  md.renderer.rules.math_block = function (tokens, idx) {
    const content = tokens[idx].content;
    try {
      return katex.renderToString(content, { displayMode: true });
    } catch (error) {
      return `$$${content}$$`;
    }
  };

  text = text.replace(/\\\[/g, "$$").replace(/\\\]/g, "$$");
  text = text.replace(/\\\(/g, "$").replace(/\\\)/g, "$");
  text = text
    .replace(/\$\$([\s\S]*?)\$\$/g, (_, formula) => renderMath(formula, true))
    .replace(/\$([^$]*?)\$/g, (_, formula) => renderMath(formula, false));

  text = text.replace(/\n(\d+)/g, "\n $1 ");

  text = text.replace(/```echarts([\s\S]*?)```/g, (_, option) => {
    const containerId = `echarts-${Math.random().toString(36).substr(2, 9)}`;
    setTimeout(() => {
      const container = document.getElementById(containerId);
      console.log("是否获取到container", container);
      if (container) {
        const chart = echarts.init(container);

        const chartOption = JSON.parse(option);
        chart.setOption(chartOption);

        const debouncedResize = debounce(() => {
          if (chart && !chart.isDisposed()) {
            chart.resize();
          }
        }, 100);

        const resizeObserver = new ResizeObserver(() => {
          debouncedResize();
        });
        resizeObserver.observe(container);

        window.addEventListener("resize", debouncedResize);

        container._echartsCleanup = () => {
          resizeObserver.disconnect();
          window.removeEventListener("resize", debouncedResize);
          if (chart && !chart.isDisposed()) {
            chart.dispose();
          }
        };
      }
    });
    return `<div id="${containerId}" class="echarts-container"></div>`;
  });

  // Mermaid 图表渲染
  text = text.replace(/```mermaid([\s\S]*?)```/g, (_, code) => {
    const mermaidId = `mermaid-${Math.random().toString(36).slice(2, 9)}`;
    setTimeout(async () => {
      const container = document.getElementById(mermaidId);
      if (!container) return;
      try {
        mermaid.initialize({
          startOnLoad: false,
          theme: "default",
          securityLevel: "loose",
          suppressErrorRendering: true,
        });
        const { svg } = await mermaid.render(`mermaid-svg-${mermaidId}`, code.trim());
        container.innerHTML = svg;
      } catch (err) {
        console.error("Mermaid渲染错误:", err);
      }
    }, 0);
    return `<div id="${mermaidId}" class="mermaid-container"></div>`;
  });

  return md.render(text);
}
